// Landing page for the public REST API (/api/v1). /docs is the full
// reference, but it is a client-rendered Scalar app, so crawlers see an empty
// page; this page carries the content search engines index. "ai voice cover
// api free" had 7,800 impressions at position 5.5 and no clicks in the 90 days
// to 2026-10-07, because /developers was a 404.
//
// Every number here comes from the code: price, retention and the response
// shape from public/openapi.json; per-second billing and the $0.10 minimum
// from the conversion webhook (API_RATE_PER_MINUTE); rate limits from
// TIER_DEFAULTS.free (models/ApiKey.ts); top-up bounds from
// pages/api/developer/checkout.ts; the Power plan's $25 from the invoice.paid
// webhook. Update them together. There is no free API balance, so never call
// the API itself free.
import React, { useState } from "react";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import Head from "next/head";
import Link from "next/link";
import { ArrowRight, Check, ChevronRight, Copy } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/StaticAccordion";
import { JamVoiceCard, SectionHeading } from "@/components/home/JamCards";

const SITE_URL = "https://www.jammable.com";
const CANONICAL = `${SITE_URL}/developers`;

const SAMPLES = [
  {
    key: "curl",
    label: "cURL",
    code: `curl -X POST https://www.jammable.com/api/v1/covers \\
  -H "Authorization: Bearer jmb_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "voice_model_id": "petergriffin",
    "audio_url": "https://example.com/vocals.mp3"
  }'`,
  },
  {
    key: "js",
    label: "JavaScript",
    code: `const res = await fetch("https://www.jammable.com/api/v1/covers", {
  method: "POST",
  headers: {
    Authorization: "Bearer jmb_live_YOUR_KEY",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    voice_model_id: "petergriffin",
    audio_url: "https://example.com/vocals.mp3",
  }),
});
const { data } = await res.json(); // status: "pending"`,
  },
  {
    key: "py",
    label: "Python",
    code: `import requests

res = requests.post(
    "https://www.jammable.com/api/v1/covers",
    headers={"Authorization": "Bearer jmb_live_YOUR_KEY"},
    json={
        "voice_model_id": "petergriffin",
        "audio_url": "https://example.com/vocals.mp3",
    },
)
cover = res.json()["data"]  # {"id": ..., "status": "pending"}`,
  },
];

const RESPONSE = `{
  "data": {
    "short_id": "jam-a3b4c5d6",
    "status": "completed",
    "voice_model_id": "petergriffin",
    "duration": 184.2,
    "output_urls": {
      "audio": "https://…/mix.mp3",
      "vocals": "https://…/vocals.mp3",
      "instrumentals": "https://…/instrumental.mp3"
    },
    "expires_at": "2026-10-18T12:00:00Z"
  }
}`;

const OUTPUTS = [
  {
    name: "Full mix",
    key: "audio",
    desc: "The finished cover, mixed and ready to play.",
  },
  {
    name: "Vocals only",
    key: "vocals",
    desc: "The converted voice on its own, for your own mix.",
  },
  {
    name: "Instrumental",
    key: "instrumentals",
    desc: "The original backing track, vocals removed.",
  },
];

const ENDPOINTS = [
  {
    method: "POST",
    path: "/api/v1/covers",
    desc: "Turn an audio file into an AI cover in any voice",
  },
  {
    method: "POST",
    path: "/api/v1/tts",
    desc: "Speak up to 2,500 characters in any voice",
  },
  {
    method: "GET",
    path: "/api/v1/covers/{id}",
    desc: "Status, progress and output files for a job",
  },
  {
    method: "GET",
    path: "/api/v1/covers",
    desc: "List the jobs you have created",
  },
  {
    method: "GET",
    path: "/api/v1/models",
    desc: "Search 100,000+ voices by name or category",
  },
  { method: "GET", path: "/api/v1/models/{id}", desc: "Details for one voice" },
  { method: "GET", path: "/api/v1/credits", desc: "Your API balance" },
  {
    method: "POST",
    path: "/api/v1/credits/checkout",
    desc: "Top up your balance",
  },
];

const PRICES = [
  { what: "A 3-minute song", cost: "$0.75" },
  { what: "A 30-second voice line", cost: "$0.13" },
  { what: "A 5-second clip", cost: "$0.10", note: "minimum per job" },
  { what: "A failed job", cost: "$0.00" },
  { what: "Searches and status checks", cost: "$0.00" },
];

const STEPS = [
  {
    title: "Create a key",
    body: (
      <>
        Sign in, open the{" "}
        <Link
          href="/developers/dashboard"
          className="tw-font-extrabold tw-text-jam-purple"
        >
          Developer Dashboard
        </Link>{" "}
        and create a jmb_live_ key. Add $10 or more of balance.
      </>
    ),
  },
  {
    title: "Start a job",
    body: (
      <>POST an audio URL to /covers, or text to /tts, with any voice ID.</>
    ),
  },
  {
    title: "Get your audio",
    body: (
      <>
        Poll the job or pass a callback_url for a webhook. Download within 7
        days.
      </>
    ),
  },
];

const FAQ = [
  {
    q: "Is there a free AI voice cover API?",
    a: "Creating an API key is free, and so are searching voices, reading voice details and checking job status. Generating audio costs $0.25 per minute, billed by the second with a $0.10 minimum per job, and only after the job completes, so failed jobs cost nothing. The Power plan includes $25 of API balance every month.",
  },
  {
    q: "How much does the Jammable API cost?",
    a: "$0.25 per minute of audio, for both covers and text-to-speech, billed by the second with a $0.10 minimum per job. Top up from the Developer Dashboard with anything from $10 to $1,000. There is no monthly fee or minimum.",
  },
  {
    q: "Which voices can I use?",
    a: "Any public voice in the Jammable library, more than 100,000 of them, plus private voices you trained or uploaded yourself. Pass a voice's ID or its URL name, like petergriffin, as voice_model_id.",
  },
  {
    q: "What audio can I send for a cover?",
    a: "A publicly accessible URL to an audio file such as MP3 or WAV. YouTube and streaming links are not supported. Jammable separates the vocals, converts them and returns the full mix, the converted vocals and the instrumental. You are responsible for having rights to the audio you submit.",
  },
  {
    q: "How do I know when a job is done?",
    a: "Poll GET /api/v1/covers/{id} until status is completed or failed, or pass a callback_url and Jammable sends a webhook when the job finishes, retried up to 3 times.",
  },
  {
    q: "What are the rate limits?",
    a: "A key can make 30 requests a minute and 500 a day, with up to 3 jobs running at once. Every response includes X-RateLimit-Limit, X-RateLimit-Remaining and X-RateLimit-Reset headers. Contact us if you need more.",
  },
  {
    q: "Can AI agents use the API?",
    a: "Yes. The Jammable MCP server gives Claude, Cursor and other MCP clients the same covers, text-to-speech and voice search, on the same key and balance.",
  },
];

// Strings in lime, comments dimmed. Enough colour to read, nothing to load.
function Code({ code }: { code: string }) {
  return (
    <>
      {code.split("\n").map((line, i) => (
        <span key={i} className="tw-block">
          {line
            .split(/("(?:[^"\\]|\\.)*"|'[^']*'|(?:#|\/\/) .*$)/)
            .map((part, j) =>
              /^["']/.test(part) ? (
                <span key={j} className="tw-text-jam-lime">
                  {part}
                </span>
              ) : /^(#|\/\/) /.test(part) ? (
                <span key={j} className="tw-text-white/40">
                  {part}
                </span>
              ) : (
                part
              ),
            )}
          {line === "" ? " " : null}
        </span>
      ))}
    </>
  );
}

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };
  return { copied, copy };
}

const MethodChip = ({ method }: { method: string }) => (
  <span
    className={`tw-inline-flex tw-w-[52px] tw-shrink-0 tw-justify-center tw-rounded-full tw-py-1 tw-text-[11px] tw-font-extrabold ${
      method === "POST"
        ? "tw-bg-jam-lime tw-text-jam-ink"
        : "tw-bg-jam-lav tw-text-jam-purple"
    }`}
  >
    {method}
  </span>
);

// Static bars standing in for a waveform; heights are fixed so server and
// client render the same markup.
const BARS = [
  5, 9, 14, 8, 18, 11, 22, 15, 9, 19, 13, 24, 10, 16, 7, 20, 12, 17, 8, 14, 21,
  10, 6, 15,
];

interface DevelopersProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  voices: any[];
}

const DevelopersPage = ({ voices }: DevelopersProps) => {
  const [tab, setTab] = useState(SAMPLES[0].key);
  const { copied, copy } = useCopy();
  const sample = SAMPLES.find((s) => s.key === tab) ?? SAMPLES[0];

  const apiLd = {
    "@context": "https://schema.org",
    "@type": "WebAPI",
    name: "Jammable API",
    description:
      "REST API for AI voice covers and text-to-speech in 100,000+ voices.",
    url: CANONICAL,
    documentation: `${SITE_URL}/docs`,
    provider: { "@type": "Organization", name: "Jammable", url: SITE_URL },
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <NextSeo
        title="AI Voice Cover & Text-to-Speech API for Developers | Jammable"
        description="Create AI voice covers and text-to-speech in 100,000+ voices with one REST API. Free API key, $0.25 per minute of audio, no charge for failed jobs."
        canonical={CANONICAL}
        openGraph={{
          title: "Jammable API: AI Voice Covers & Text-to-Speech",
          description:
            "One REST API for AI covers and text-to-speech in 100,000+ voices.",
          url: CANONICAL,
          type: "website",
          siteName: "Jammable",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(apiLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
        />
      </Head>

      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-12 tw-bg-white tw-px-4 tw-pb-16 tw-pt-2 tw-text-jam-ink md:tw-gap-16 md:tw-px-8 md:tw-pt-4">
        {/* ── Hero ───────────────────────────────────────────────── */}
        <section className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-8 tw-overflow-hidden tw-rounded-[34px] tw-bg-jam-ink tw-p-5 tw-pt-9 tw-text-white md:tw-rounded-[44px] md:tw-p-10 md:tw-pl-12 lg:tw-grid-cols-[1fr_1.1fr] lg:tw-gap-12">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-5 tw-px-1 md:tw-px-0">
            <span className="tw-rounded-full tw-bg-jam-deep tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.14em] tw-text-jam-soft">
              Jammable API
            </span>
            <h1 className="jam-display tw-m-0 tw-text-[42px] tw-font-extrabold tw-leading-[1.02] md:tw-text-[64px] md:tw-leading-none">
              AI voice covers,{" "}
              <span className="tw-whitespace-nowrap tw-rounded-[14px] tw-bg-jam-lime tw-px-2 tw-pb-0.5 tw-text-jam-ink md:tw-rounded-[22px] md:tw-px-3.5 md:tw-pb-1">
                by API
              </span>
            </h1>
            <p className="tw-m-0 tw-max-w-xl tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-jam-soft md:tw-text-lg">
              Covers and text-to-speech in 100,000+ voices from your own app.
              One REST call starts a job; you get back the mix, the vocals and
              the instrumental.
            </p>
            <div className="tw-flex tw-flex-wrap tw-gap-2.5">
              <Link
                href="/developers/dashboard"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-jam-lime tw-px-6 tw-py-3.5 tw-text-[15px] tw-font-extrabold tw-text-jam-ink tw-no-underline tw-transition-transform hover:tw--translate-y-0.5"
              >
                Get your API key
                <ArrowRight size={17} strokeWidth={2.6} />
              </Link>
              <Link
                href="/docs"
                className="tw-inline-flex tw-items-center tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-deep tw-px-6 tw-py-3 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline tw-transition-colors hover:tw-border-jam-soft"
              >
                API reference
              </Link>
            </div>
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-5 tw-gap-y-2 tw-p-0 tw-text-[13px] tw-font-bold tw-text-jam-soft">
              {[
                "$0.25 per minute",
                "Billed by the second",
                "Failed jobs are free",
              ].map((t) => (
                <li key={t} className="tw-flex tw-items-center tw-gap-1.5">
                  <Check
                    size={15}
                    strokeWidth={3}
                    className="tw-text-jam-lime"
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="tw-min-w-0 tw-overflow-hidden tw-rounded-[26px] tw-bg-[#1F0938] tw-ring-1 tw-ring-white/10">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-white/10 tw-px-3 tw-py-2.5">
              <div
                className="tw-flex tw-gap-1"
                role="tablist"
                aria-label="Code language"
              >
                {SAMPLES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === s.key}
                    onClick={() => setTab(s.key)}
                    className={`tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-extrabold tw-transition-colors sm:tw-px-3.5 ${
                      tab === s.key
                        ? "tw-bg-white tw-text-jam-ink"
                        : "tw-bg-transparent tw-text-jam-soft hover:tw-text-white"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => copy(sample.code, "hero")}
                aria-label="Copy code"
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-bg-jam-deep tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-white hover:tw-bg-jam-purple"
              >
                {copied === "hero" ? (
                  <Check size={13} strokeWidth={3} />
                ) : (
                  <Copy size={13} strokeWidth={2.6} />
                )}
                <span className="tw-hidden sm:tw-inline">
                  {copied === "hero" ? "Copied" : "Copy"}
                </span>
              </button>
            </div>
            <pre className="tw-m-0 tw-h-[300px] tw-overflow-auto tw-p-5 tw-text-[12.5px] tw-leading-[1.7] tw-text-white md:tw-text-[13px]">
              <code>
                <Code code={sample.code} />
              </code>
            </pre>
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-white/10 tw-bg-black/20 tw-px-5 tw-py-3.5">
              <span className="tw-shrink-0 tw-rounded-full tw-bg-jam-lime tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-extrabold tw-text-jam-ink">
                201
              </span>
              <code className="tw-truncate tw-text-[12.5px] tw-text-jam-soft">
                {`{"data": {"short_id": "jam-a3b4c5d6", "status": "pending"}}`}
              </code>
            </div>
          </div>
        </section>

        {/* ── Voices ─────────────────────────────────────────────── */}
        {voices.length > 0 && (
          <section className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
              <div>
                <span className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.16em] tw-text-jam-purple">
                  voice_model_id
                </span>
                <h2 className="jam-display tw-m-0 tw-mt-1 tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
                  Any of 100,000+ voices
                </h2>
                <p className="tw-m-0 tw-mt-1 tw-text-sm tw-font-semibold tw-text-jam-muted">
                  Copy an ID, or find one with GET /api/v1/models. Your own
                  private voices work too.
                </p>
              </div>
              <Link
                href="/models"
                className="tw-whitespace-nowrap tw-rounded-full tw-bg-jam-lav tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
              >
                Browse all &rarr;
              </Link>
            </div>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-6 md:tw-gap-3.5">
              {voices.map((v) => (
                <div
                  key={v._id}
                  className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5"
                >
                  <JamVoiceCard model={v} />
                  <button
                    type="button"
                    onClick={() => copy(v.urlParam, v._id)}
                    title="Copy voice_model_id"
                    className="tw-flex tw-min-w-0 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-2.5 tw-py-1.5 tw-text-left tw-text-[11px] tw-font-bold tw-text-jam-deep hover:tw-bg-jam-line"
                  >
                    {copied === v._id ? (
                      <Check
                        size={12}
                        strokeWidth={3}
                        className="tw-shrink-0"
                      />
                    ) : (
                      <Copy
                        size={12}
                        strokeWidth={2.6}
                        className="tw-shrink-0"
                      />
                    )}
                    <code className="tw-truncate">
                      {copied === v._id ? "Copied" : v.urlParam}
                    </code>
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── What you get back ──────────────────────────────────── */}
        <section className="tw-grid tw-grid-cols-1 tw-gap-6 tw-rounded-[34px] tw-bg-jam-lav tw-p-5 md:tw-rounded-[44px] md:tw-p-10 lg:tw-grid-cols-2 lg:tw-gap-10">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
            <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold tw-leading-[1.05] md:tw-text-[34px]">
              Three files back from every cover
            </h2>
            <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
              Send any audio file. Jammable separates the vocals, converts them
              into the voice you chose and mixes the cover back together.
              Text-to-speech jobs return the spoken audio.
            </p>
            <div className="tw-flex tw-flex-col tw-gap-2.5">
              {OUTPUTS.map((o, i) => (
                <div
                  key={o.key}
                  className="tw-flex tw-items-center tw-gap-4 tw-rounded-[22px] tw-bg-white tw-p-4"
                >
                  <div
                    className="tw-flex tw-h-12 tw-w-[92px] tw-shrink-0 tw-items-center tw-gap-[3px]"
                    aria-hidden
                  >
                    {BARS.map((h, j) => (
                      <span
                        key={j}
                        style={{
                          height: Math.max(
                            4,
                            (h * (i === 1 ? 0.8 : i === 2 ? 0.65 : 1) * 48) /
                              24,
                          ),
                        }}
                        className={`tw-w-[2px] tw-rounded-full ${i === 0 ? "tw-bg-jam-purple" : i === 1 ? "tw-bg-jam-ink" : "tw-bg-jam-soft"}`}
                      />
                    ))}
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      <h3 className="tw-m-0 tw-text-base tw-font-extrabold">
                        {o.name}
                      </h3>
                      <code className="tw-rounded-full tw-bg-jam-lav tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-jam-deep">
                        {o.key}
                      </code>
                    </div>
                    <p className="tw-m-0 tw-mt-0.5 tw-text-[13px] tw-font-semibold tw-text-jam-muted">
                      {o.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="tw-min-w-0 tw-overflow-hidden tw-rounded-[26px] tw-bg-jam-ink">
            <div className="tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-white/10 tw-px-5 tw-py-3 tw-text-xs tw-font-extrabold tw-text-jam-soft">
              <MethodChip method="GET" />
              <code className="tw-truncate tw-text-white">
                /api/v1/covers/jam-a3b4c5d6
              </code>
            </div>
            <pre className="tw-m-0 tw-overflow-x-auto tw-p-5 tw-text-[12.5px] tw-leading-[1.7] tw-text-white">
              <code>
                <Code code={RESPONSE} />
              </code>
            </pre>
          </div>
        </section>

        {/* ── Endpoints ──────────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading
            title="Endpoints"
            aside={
              <Link
                href="/docs"
                className="tw-whitespace-nowrap tw-text-sm tw-font-extrabold tw-text-jam-purple tw-no-underline"
              >
                Full reference &rarr;
              </Link>
            }
          />
          <div className="tw-overflow-hidden tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line">
            {ENDPOINTS.map((e, i) => (
              <div
                key={e.method + e.path}
                className={`tw-flex tw-flex-col tw-gap-1.5 tw-px-5 tw-py-4 sm:tw-flex-row sm:tw-items-center sm:tw-gap-4 ${
                  i
                    ? "tw-border-0 tw-border-t-2 tw-border-solid tw-border-jam-line"
                    : ""
                }`}
              >
                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 sm:tw-w-[340px] sm:tw-shrink-0">
                  <MethodChip method={e.method} />
                  <code className="tw-truncate tw-text-[13.5px] tw-font-bold tw-text-jam-ink">
                    {e.path}
                  </code>
                </div>
                <span className="tw-text-sm tw-font-semibold tw-text-jam-muted">
                  {e.desc}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Pricing ────────────────────────────────────────────── */}
        <section className="tw-grid tw-grid-cols-1 tw-gap-3.5 lg:tw-grid-cols-[0.9fr_1.1fr]">
          <div className="tw-flex tw-flex-col tw-justify-between tw-gap-6 tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-rounded-[40px] md:tw-p-9">
            <div>
              <span className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.16em] tw-text-jam-soft">
                Pricing
              </span>
              <h2 className="jam-display tw-m-0 tw-mt-2 tw-text-[56px] tw-font-extrabold tw-leading-none md:tw-text-[72px]">
                $0.25
              </h2>
              <p className="tw-m-0 tw-mt-2 tw-text-lg tw-font-bold tw-text-jam-soft">
                per minute of audio, covers or speech
              </p>
            </div>
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0 tw-text-sm tw-font-semibold tw-text-jam-soft">
              <li>Billed by the second, after the job completes</li>
              <li>Top up from $10 to $1,000. No monthly fee</li>
              <li>
                The{" "}
                <Link
                  href="/pricing"
                  className="tw-font-extrabold tw-text-jam-lime tw-no-underline"
                >
                  Power plan
                </Link>{" "}
                includes $25 of API balance every month
              </li>
            </ul>
          </div>
          <div className="tw-flex tw-flex-col tw-rounded-[34px] tw-border-2 tw-border-solid tw-border-jam-line tw-px-6 tw-py-3 md:tw-rounded-[40px] md:tw-px-9 md:tw-py-5">
            {PRICES.map((p, i) => (
              <div
                key={p.what}
                className={`tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-4 ${
                  i
                    ? "tw-border-0 tw-border-t-2 tw-border-solid tw-border-jam-line"
                    : ""
                }`}
              >
                <span className="tw-text-[15px] tw-font-bold">
                  {p.what}
                  {p.note && (
                    <span className="tw-ml-2 tw-text-xs tw-font-bold tw-text-jam-muted">
                      {p.note}
                    </span>
                  )}
                </span>
                <span className="jam-display tw-text-2xl tw-font-extrabold">
                  {p.cost}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Quickstart ─────────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading title="Your first cover in three steps" />
          <ol className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-2.5 tw-p-0 md:tw-grid-cols-3 md:tw-gap-3.5">
            {STEPS.map((s, i) => (
              <li
                key={s.title}
                className="tw-flex tw-flex-col tw-gap-2 tw-rounded-[28px] tw-bg-jam-lav tw-p-6"
              >
                <span className="jam-display tw-mb-2 tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-lg tw-font-extrabold tw-text-jam-ink">
                  {i + 1}
                </span>
                <h3 className="jam-display tw-m-0 tw-text-xl tw-font-extrabold">
                  {s.title}
                </h3>
                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Agents ─────────────────────────────────────────────── */}
        <Link
          href="/mcp"
          className="tw-group tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-4 tw-rounded-[34px] tw-bg-jam-lime tw-p-6 tw-text-jam-ink tw-no-underline md:tw-flex-row md:tw-items-center md:tw-rounded-[40px] md:tw-px-9 md:tw-py-7"
        >
          <div>
            <h2 className="jam-display tw-m-0 tw-text-[24px] tw-font-extrabold md:tw-text-[28px]">
              Building with AI agents?
            </h2>
            <p className="tw-m-0 tw-mt-1 tw-max-w-xl tw-text-sm tw-font-bold tw-text-jam-ink/70">
              The Jammable MCP server gives Claude, Cursor and other agents the
              same covers, speech and voice search, on the same key.
            </p>
          </div>
          <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-jam-ink tw-px-5 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-transition-transform group-hover:tw-translate-x-0.5">
            MCP server
            <ChevronRight size={17} strokeWidth={2.6} />
          </span>
        </Link>

        {/* ── FAQ ────────────────────────────────────────────────── */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <SectionHeading title="Frequently asked questions" />
          <Accordion
            type="single"
            collapsible
            className="tw-w-full tw-rounded-[28px] tw-bg-jam-lav tw-px-5 md:tw-px-7"
          >
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="tw-text-left tw-text-base tw-font-extrabold tw-text-jam-ink">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>
    </>
  );
};

export default DevelopersPage;

export const getServerSideProps: GetServerSideProps = async ({ res }) => {
  res.setHeader(
    "Cache-Control",
    "public, s-maxage=3600, stale-while-revalidate=7200",
  );
  try {
    const { fetchTopHubModels } = await import("@/utils/hubModels");
    const top = await fetchTopHubModels(12, { allTime: true });
    // Only what JamVoiceCard reads.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const voices = top.map((m: any) => ({
      _id: m._id,
      urlParam: m.urlParam,
      artistname: m.artistname,
      uses: m.uses,
      image: {
        url: m.image?.url ?? null,
        colorPalette: { LightMuted: m.image?.colorPalette?.LightMuted ?? null },
      },
      user: { username: m.user?.username ?? null },
    }));
    return { props: { voices } };
  } catch (error) {
    console.error("Developers SSR error:", error);
    return { props: { voices: [] } };
  }
};
